import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AIRobotControl: React.FC = () => {
  const navigate = useNavigate();
  const exampleRef = useRef<HTMLDivElement>(null);
  const practiceRef = useRef<HTMLDivElement>(null);
  const [showPdf, setShowPdf] = useState(false);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      overflow: 'auto',
      padding: '1rem'
    }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          <button
          onClick={() => navigate('/class-materials/ai-robot-project')}
            style={{
              marginRight: '1rem',
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← 돌아가기
          </button>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>AI 모델 연동 후 로봇 주행 테스트하기</h1>
        </div>
        
        <div style={{ marginBottom: '1rem', border: '1px solid #ccc', padding: '0.8rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontWeight: 'bold', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
            📚 목차
          </h2>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          <li style={{marginBottom: '8px'}}>
              <a href="#로봇 제어 시스템 개요" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                로봇 제어 시스템 개요
              </a>
            </li>
            <li style={{marginBottom: '8px'}}>
              <a href="#송신부 코드" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                송신부 코드 (노트북 → 마이크로비트)
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
              <a href="#수신부 코드" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                수신부 코드 (로봇 → 마이크로비트)
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
              <a href="#AI 모델 연동" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                AI 모델 연동하기
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
              <a href="#통신 테스트" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                통신 테스트
            </a>
          </li>
        </ul>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'end', marginBottom: '1rem', gap: '1rem' }}>
          <p style={{ color: '#666', margin: 0 }}>
            본 섹션에서는 AI 모델을 활용한 로봇 제어 시스템을 구현합니다. 
            송신부(노트북)와 수신부(로봇)로 나누어진 마이크로비트 코드를 통해 
            AI 모델의 판단에 따라 로봇을 원격으로 제어하는 방법을 학습합니다.
          </p>
        </div>
        
        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="로봇 제어 시스템 개요">
          🤖 로봇 제어 시스템 개요
        </h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          AI 모델을 활용한 로봇 제어 시스템은 다음과 같이 구성됩니다:<br/>
          <strong>송신부</strong>: 노트북에서 AI 모델이 판단한 명령을 마이크로비트로 전송<br/>
          <strong>수신부</strong>: 로봇에 장착된 마이크로비트가 명령을 받아 서보모터 제어<br/>
          <strong>통신</strong>: 무선 통신을 통해 실시간으로 로봇 제어
        </p>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#f0f8ff', marginBottom: '1rem' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#1976d2', textAlign: 'center' }}>시스템 구조도:</h4>
          <img
            src="/images/microbit/AIRobotProject/robotcontrol.png"
            alt="로봇 제어 시스템 개요도"
            style={{ 
              width: '100%', 
              height: 'auto', 
              borderRadius: '8px', 
              display: 'block', 
              margin: '0 auto',
              maxWidth: '800px'
            }}
          />
        </div>
        
        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="송신부 코드">
          📡 송신부 코드 (노트북 → 마이크로비트)
        </h2>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: 'rgba(255, 220, 175, 0.8)', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: 700, color: '#1976d2', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
            1. 마이크로비트 조종 코드
          </h3>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            노트북에서 AI 모델의 판단에 따라 마이크로비트로 명령을 전송하는 송신부 코드입니다.<br/>
            <a href="https://makecode.microbit.org/S18017-76778-90033-30174" target="_blank" rel="noopener noreferrer"><b>송신부 코드 링크</b></a>를 클릭하여 코드를 확인해보세요.
          </p>
          <div style={{ position: 'relative', height: 'calc(300px + 5em)', width: '100%', overflow: 'hidden' }}>
            <iframe 
              style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%' }} 
              src="https://makecode.microbit.org/---codeembed#pub:S18017-76778-90033-30174" 
              allowFullScreen 
              frameBorder="0" 
              sandbox="allow-scripts allow-same-origin allow-popups"
            ></iframe>
          </div>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            <strong>송신부 기능:</strong><br/>
            • AI 모델로부터 받은 명령을 무선으로 전송<br/>
            • 다양한 제어 명령 (전진, 후진, 좌회전, 우회전, 정지)<br/>
            • 실시간 명령 전송 및 상태 확인
          </p>
        </div>

        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="수신부 코드">
          📻 수신부 코드 (로봇 → 마이크로비트)
        </h2>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: 'rgba(255, 220, 175, 0.8)', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: 700, color: '#1976d2', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
            2. 로봇 마이크로비트 수신부 코드
          </h3>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            로봇에 장착된 마이크로비트가 송신부로부터 명령을 받아 서보모터를 제어하는 수신부 코드입니다.<br/>
            <a href="https://makecode.microbit.org/_Yky7ipCF1i4t" target="_blank" rel="noopener noreferrer"><b>수신부 코드 링크</b></a>를 클릭하여 코드를 확인해보세요.
          </p>
          <div style={{ position: 'relative', height: 'calc(300px + 5em)', width: '100%', overflow: 'hidden' }}>
            <iframe 
              style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%' }} 
              src="https://makecode.microbit.org/---codeembed#pub:_Yky7ipCF1i4t" 
              allowFullScreen 
              frameBorder="0" 
              sandbox="allow-scripts allow-same-origin allow-popups"
            ></iframe>
          </div>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            <strong>수신부 기능:</strong><br/>
            • 송신부로부터 무선 명령 수신<br/>
            • 받은 명령에 따라 서보모터 제어<br/>
            • 로봇의 실제 움직임 구현 (전진, 후진, 좌회전, 우회전, 정지)
          </p>
        </div>

        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="AI 모델 연동">
          🤖 AI 모델 연동하기
        </h2>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: 'rgba(255, 220, 175, 0.8)', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: 700, color: '#1976d2', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
            3. MakeAI Robots 웹사이트에서 AI 모델 연동
          </h3>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            코드를 마이크로비트에 업로드한 후, AI 모델과 연동하여 실제 로봇을 제어합니다.<br/>
            <a href="https://makeairobots.com/" target="_blank" rel="noopener noreferrer"><b>MakeAI Robots 웹사이트</b></a>에 접속하여 AI 모델을 연동해보세요.
          </p>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#e8f5e8', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32' }}>🌐 MakeAI Robots 사용 방법:</h4>
            <ol style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li><a href="https://makeairobots.com/" target="_blank" rel="noopener noreferrer">https://makeairobots.com/</a> 접속</li>
              <li>3. Connect your micro:bit to your AI! 버튼 클릭 후 마이크로비트 연결</li>
              <li>카메라 권한 허용</li>
              <li>송신부 마이크로비트와 연결</li>
              <li>AI 모델이 인식한 결과에 따라 로봇 제어</li>
            </ol>
          </div>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#fff3cd', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>💡 팁:</h4>
            <ul style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li>송신부 마이크로비트가 노트북에 연결되어 있어야 합니다</li>
              <li>수신부 마이크로비트가 로봇에 장착되어 있어야 합니다</li>
              <li>카메라가 AI 모델이 인식할 대상이 잘 보이도록 위치를 조정하세요</li>
              <li>조명이 충분한 환경에서 테스트하세요</li>
            </ul>
          </div>
        </div>

        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="통신 테스트">
          🧪 통신 테스트
        </h2>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: 'rgba(255, 220, 175, 0.8)', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: 700, color: '#1976d2', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
            4. 시스템 테스트 방법
          </h3>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            송신부와 수신부 코드를 각각 다른 마이크로비트에 업로드한 후 통신 테스트를 진행합니다.ㅁ
          </p>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#f5f5f5', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2' }}>테스트 순서:</h4>
            <ol style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li>송신부 코드를 첫 번째 마이크로비트에 업로드</li>
              <li>수신부 코드를 두 번째 마이크로비트에 업로드</li>
              <li>두 마이크로비트를 전원에 연결</li>
              <li>송신부 마이크로비트의 버튼을 눌러 명령 전송</li>
              <li>수신부 마이크로비트가 연결된 로봇의 동작 확인</li>
            </ol>
          </div>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#fff3cd', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>⚠️ 주의사항:</h4>
            <ul style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li>두 마이크로비트 모두 전원이 연결되어 있어야 합니다</li>
              <li>무선 통신이 원활하게 이루어지는 거리 내에서 테스트하세요</li>
              <li>서보모터가 올바르게 연결되어 있는지 확인하세요</li>
              <li>PCA9685 확장 보드가 제대로 연결되어 있는지 확인하세요</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  export default AIRobotControl;
